/**
 * @file finish-handler.ts
 * @description Finalizes drawing operations, manages the undo/redo history recording, and handles the transition between ghost-edit and rasterization.
 */

import { g, Tool } from '@hcie/core';
import { getActiveLayer } from '../layer-service';
import { renderLayers } from '../render-engine';
import { updateLayerPanel } from '../layers';
import { simplifyPath, buildLassoSelection } from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';
import { applySelectionMaskToCtx } from './selection-utils';
import { updateCanvasCursor } from '../cursor-manager';

// System-level global declarations for history management removed. Using window.historyManager.

export class FinishHandler {
    /**
     * @function finishDrawing
     * @description Master termination function for all active canvas interactions.
     * @param {MouseEvent | null} event - The mouse event that triggered the finish (or null for programmatic finish).
     * @returns {boolean} - Returns true if a drawing operation was finalized.
     */
    public static finishDrawing(event: MouseEvent | null): boolean {
        // [CRITICAL_LOGIC_START]
        const isInteracting: boolean = g.drawing || (g as any)._isResizingCrop || (g as any)._isMovingCrop || g.movingSelection;
        if (!isInteracting) {
            return false;
        }

        const activeLayer = getActiveLayer();
        if (!activeLayer) {
            return false;
        }

        const isShiftPressed: boolean = event ? event.shiftKey : false;
        const isAltPressed: boolean = event ? event.altKey : false;

        try {
            // CASE A: Transformation/Movement finish (no raster undo here)
            if (g.current_tool.id === Tool.MoveSelection.id || g.current_tool.id === Tool.MoveContent.id) {
                this.finalizeMovement();
                return true;
            }

            // CASE B: Tool-Specific logic delegators
            if (g.current_tool.id === Tool.Lasso.id) {
                this.finalizeLasso(isShiftPressed, isAltPressed);
                return true;
            }
            if (g.current_tool.id === Tool.Crop.id) {
                this.finalizeCrop();
                return true;
            }
            if (g.current_tool.id === Tool.PolySelect.id) {
                if (g.movingSelection) {
                    this.finalizeMovement();
                }
                return true;
            }

            // CASE C: Vector/Raster Hybrid Logic (Ghost Mode)
            const isVectorTool: boolean = [
                Tool.Line.id, Tool.Circle.id, Tool.Rectangle.id, 
                Tool.Rounded_Rectangle.id, Tool.Ellipse.id
            ].includes(g.current_tool.id);
            
            const isVectorLayer: boolean = activeLayer.type === 'vector';

            if (isVectorTool) {
                if (this.handleVectorGhostTransition(isVectorLayer, activeLayer)) {
                    return true;
                }
            }

            // CASE D: Universal Raster Commit
            this.commitRasterAction(activeLayer);

        } catch (error) {
            console.error("[FinishHandler] Critical failure during drawing finalization:", error);
        } finally {
            this.resetInteractionState();
        }
        
        return true;
        // [CRITICAL_LOGIC_END]
    }

    // --- Internal Finalization Methods ---

    /**
     * @function finalizeMovement
     * @private
     */
    private static finalizeMovement(): void {
        g.movingSelection = false;
        g.drawing = false;
        renderLayers();
    }

    /**
     * @function finalizeLasso
     * @private
     */
    private static finalizeLasso(shift: boolean, alt: boolean): void {
        const lassoPoints = (window as any).lassoPoints;
        if (g.movingSelection) {
            g.movingSelection = false;
        } else if (lassoPoints) {
            let mode: SelectionMode = "replace";
            if (shift) mode = "add";
            if (alt) mode = "subtract";
            
            const smoothing: number = (g as any).settings?.selection?.lassoSmoothing || 0;
            let finalPoints = lassoPoints;
            if (smoothing > 0.1) {
                finalPoints = simplifyPath(lassoPoints, smoothing);
            }
            
            g.selectionPreviewBorder = [];
            buildLassoSelection(finalPoints, mode);

            if ((g as any).settings?.selection?.ghostMode) {
                (g as any)._lassoEditPoints = [...finalPoints];
                (g as any)._lastSelectionMode = mode;
                (window as any).lassoPoints = [...finalPoints];
            } else {
                (window as any).lassoPoints = [];
                g.drawing = false;
            }
        }
        
        g.isSelectionActive = true;
        if ((window as any).tempCtx) {
            (window as any).tempCtx.clearRect(0, 0, (window as any).tempCtx.canvas.width, (window as any).tempCtx.canvas.height);
        }
        
        // Ensure status bar appears immediately if ghost mode is active
        if ((g as any).settings?.selection?.ghostMode && g.isSelectionActive) {
            const drawingInfo = document.getElementById('drawingInfo');
            if (drawingInfo) drawingInfo.style.display = 'flex';
        }

        renderLayers();
    }

    /**
     * @function finalizeCrop
     * @private
     */
    private static finalizeCrop(): void {
        const globalState = (g as any);
        if (!globalState._isResizingCrop && !globalState._isMovingCrop) {
            const x = Math.min(g.startX, g.pX);
            const y = Math.min(g.startY, g.pY);
            const width = Math.abs(g.pX - g.startX);
            const height = Math.abs(g.pY - g.startY);
            
            if (width > 2 && height > 2) {
                globalState.lastCropRect = { x, y, w: width, h: height };
            }
        }
        globalState._isResizingCrop = false;
        globalState._isMovingCrop = false;
        g.drawing = false;
        renderLayers();
    }

    /**
     * @function handleVectorGhostTransition
     * @description Determines if a vector operation should enter Ghost Edit mode on the DOM overlay.
     * @private
     */
    private static handleVectorGhostTransition(isVectorLayer: boolean, activeLayer: any): boolean {
        const vtm = (window as any).vectorToolManager;
        const isGhostModeEnabled: boolean = (g as any).settings?.vector?.ghostMode || isVectorLayer;

        if (isGhostModeEnabled && vtm && typeof vtm.startNewFromTool === 'function') {
            if (!isVectorLayer && activeLayer.ctx) {
                // Buffer the raster state before ghosting in case of discard
                this.prepareVectorRasterGhost(activeLayer);
            }
            vtm.startNewFromTool(g.current_tool.id, g.startX, g.startY, g.pX, g.pY, g.activeLayerIndex);
            
            if ((window as any).tempCtx) {
                (window as any).tempCtx.clearRect(0, 0, (window as any).tempCtx.canvas.width, (window as any).tempCtx.canvas.height);
            }
            return true;
        }
        return false;
    }

    /**
     * @function prepareVectorRasterGhost
     * @private
     */
    private static prepareVectorRasterGhost(activeLayer: any): void {
        try {
            (window as any).vectorRasterGhost = {
                layerIndex: g.activeLayerIndex,
                prev: activeLayer.ctx.getImageData(0, 0, g.image_width, g.image_height),
                desc: 'Vector Shape',
            };
        } catch {
            (window as any).vectorRasterGhost = { layerIndex: g.activeLayerIndex, prev: null, desc: 'Vector Shape' };
        }
    }

    /**
     * @function commitRasterAction
     * @description Applies temporary drawing buffer to the active layer's permanent context.
     * @private
     */
    private static commitRasterAction(activeLayer: any): void {
        const tCanvas = (window as any).tempCanvas;
        const tCtx = (window as any).tempCtx;
        
        if (!tCanvas || !tCtx || !activeLayer.ctx) {
            return;
        }

        applySelectionMaskToCtx(tCtx);

        // [CRITICAL_LOGIC_START]
        // Reset composite operation to avoid destination-in artifacts from selection masks
        activeLayer.ctx.save();
        activeLayer.ctx.globalCompositeOperation = 'source-over';
        activeLayer.ctx.globalAlpha = 1.0;

        if (g.current_tool.id === Tool.Eraser.id) {
            activeLayer.ctx.globalCompositeOperation = 'destination-out';
            activeLayer.ctx.drawImage(tCanvas as HTMLCanvasElement, 0, 0);
        } else {
            activeLayer.ctx.drawImage(tCanvas as HTMLCanvasElement, 0, 0);
        }
        activeLayer.ctx.restore();
        // [CRITICAL_LOGIC_END]

        this.recordUndoHistory(activeLayer);
    }

    /**
     * @function resetInteractionState
     * @description Cleans up global flags and UI cursor states after an action.
     * @private
     */
    private static resetInteractionState(): void {
        const tCtx = (window as any).tempCtx;
        g.drawing = false;
        g.movingSelection = false;
        (g as any)._isResizingCrop = false;
        (g as any)._isMovingCrop = false;
        
        updateCanvasCursor();
        
        if (tCtx) {
            tCtx.clearRect(0, 0, tCtx.canvas.width, tCtx.canvas.height);
        }
        
        (g as any).needsRender = true;
        updateLayerPanel();
    }

    /**
     * @function recordUndoHistory
     * @private
     */
    private static recordUndoHistory(layer: any): void {
        if (!layer || !layer.ctx || !g.previousLayerState) {
            return;
        }

        const newImageData = layer.ctx.getImageData(0, 0, g.image_width, g.image_height);
        const description: string = this.getToolFriendlyName();
        
        // Use global history manager bridge established in ui-coordinator
        const hManager = (window as any).historyManager;
        const makeNamed = (window as any).makeNamedDrawAction;
        
        if (hManager && typeof makeNamed === 'function') {
            const action = makeNamed(g.activeLayerIndex, g.previousLayerState, newImageData, description);
            hManager.push(action);
        } else {
            // Log missing bridges for stabilization debugging (Bug 47)
            if (!hManager) console.warn('[FinishHandler] historyManager is missing for undo record.');
            if (typeof makeNamed !== 'function') console.warn('[FinishHandler] makeNamedDrawAction bridge is missing.');
        }
        
        g.previousLayerState = null;
    }

    /**
     * @function getToolFriendlyName
     * @private
     */
    private static getToolFriendlyName(): string {
        const id = g.current_tool.id;
        switch (id) {
            case 'btn-red-eye': return 'Red Eye Removal';
            case Tool.Eraser.id: return 'Eraser';
            case Tool.Pen.id: return 'Pen';
            case Tool.Brush.id: return 'Brush';
            case Tool.Spray.id: return 'Spray';
            case Tool.Flood_Fill.id: return 'Fill';
            default: return 'Draw';
        }
    }
}
